import { useEffect, useId, useState } from 'react'
import { ModalOverlay } from './ui/ModalOverlay'
import { PrimaryButton, SecondaryButton } from './ui'

interface TutorialModalProps {
  currentStep: number
  totalSteps: number
  onNext: () => void
  onSkip: () => void
  title: string
  description: string
  imageUrl?: string
}

function PaginationDot({ isActive }: { isActive: boolean }) {
  return (
    <div
      className={`size-[10px] shrink-0 rounded-full ${
        isActive ? 'bg-foreground-brand-primary-alt' : 'bg-border-secondary'
      }`}
      aria-hidden
    />
  )
}

export default function TutorialModal({
  currentStep,
  totalSteps,
  onNext,
  onSkip,
  title,
  description,
  imageUrl,
}: TutorialModalProps) {
  const titleId = useId()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setIsVisible(true)
  }, [])

  return (
    <ModalOverlay
      open
      onClose={onSkip}
      ariaLabelledBy={titleId}
      className={`transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      panelClassName="flex w-full max-w-[480px] flex-col items-center overflow-clip rounded-2xl bg-background-primary shadow-xl"
    >
      {imageUrl ? (
        <div className="relative flex w-full shrink-0 flex-col items-center gap-0 px-[16px] pt-[16px]">
          <div className="relative h-[208px] w-full shrink-0 overflow-clip rounded-md">
            <img
              alt=""
              className="pointer-events-none absolute inset-0 size-full max-w-none rounded-md object-cover"
              src={imageUrl}
            />
          </div>
        </div>
      ) : null}

      <div className="relative flex w-full shrink-0 flex-col items-center">
        <div className="relative flex w-full shrink-0 flex-col items-center gap-[16px] px-[16px] pt-[20px] text-center">
          <div className="relative flex w-full shrink-0 flex-col items-center gap-[2px]">
            <h2
              id={titleId}
              className="relative w-full shrink-0 font-display text-md font-semibold leading-[32px] text-text-primary-900"
            >
              {title}
            </h2>
            <p className="relative w-full shrink-0 font-body text-sm font-regular leading-[24px] text-text-tertiary-600">
              {description}
            </p>
          </div>
        </div>
        <div className="h-[20px] w-full shrink-0" />
      </div>

      <div
        className="relative flex shrink-0 items-center justify-center gap-[16px]"
        role="group"
        aria-label={`Tutorial step ${currentStep} of ${totalSteps}`}
      >
        {Array.from({ length: totalSteps }).map((_, index) => (
          <PaginationDot key={index} isActive={index === currentStep - 1} />
        ))}
      </div>

      <div className="relative flex w-full shrink-0 flex-col items-start pt-[24px]">
        <div className="relative flex w-full shrink-0 cursor-pointer flex-col items-start gap-[12px] px-[16px] pb-[16px]">
          <PrimaryButton onClick={onNext} fullWidth>
            {currentStep === totalSteps ? 'Go to my dashboard' : 'Next'}
          </PrimaryButton>
          <div
            className={`w-full transition-opacity duration-300 ${
              currentStep < totalSteps ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-hidden={currentStep >= totalSteps}
          >
            <SecondaryButton onClick={onSkip} fullWidth disabled={currentStep >= totalSteps}>
              Skip
            </SecondaryButton>
          </div>
        </div>
      </div>
    </ModalOverlay>
  )
}
